import {UploadsMediaType} from '@Panels/user-media-panel/user-media-panel.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import type {DeferredImageUploadData, ImageData, AddMediaItem, VideoData} from '@Libraries/add-media-library';
import {ElementDataType, isDeferredImageUploadData} from '@Libraries/add-media-library';
import {storeDeferredEditorUploads} from '@Libraries/deferred-editor-uploads.library';
import {openUserMediaModal} from '@Modals/user-media-modal';
import {isUserLoggedIn} from '@Libraries/user.library';
import {openErrorModal} from '@Modals/error-modal';
import type {CreateDesignFromMediaModalOpts} from '@Components/design-selector/design-selector.types';
import {openAuthenticateModal} from '@Modals/authenticate-modal/authenticate-modal-library';
import {openStockMediaModal} from '@Modals/stock-modal';
import {StockPanelTabs} from '@Panels/stock-panel/stock-panel.types';

type MediaItemForPosterCreation = ImageData | VideoData | DeferredImageUploadData;

interface CreatePostersConfig {
  opts: CreateDesignFromMediaModalOpts;
  allCreatedPosterIds: string[];
  failedUploads: File[];
  accumulatedItemsForMultipage: MediaItemForPosterCreation[];
}

interface SharedPosterCreationLogic {
  onAddSelected: (items: AddMediaItem[], currentUploadIndex?: number, totalUploadsCount?: number) => Promise<void>;
  onErrorDuringUpload: (file: File, currentUploadIndex?: number, totalItemsToUpload?: number) => void;
}

const isMediaItemForPosterCreation = (item: AddMediaItem): item is MediaItemForPosterCreation => {
  if (isDeferredImageUploadData(item)) {
    return true;
  }
  return (item.type === ElementDataType.IMAGE || item.type === ElementDataType.VIDEO) && 'hashedFilename' in item;
};

const getSharedPosterCreationLogic = ({opts, allCreatedPosterIds, failedUploads, accumulatedItemsForMultipage}: CreatePostersConfig): SharedPosterCreationLogic => {
  const createSinglePagePostersFromItems = async (items: MediaItemForPosterCreation[], currentIndex?: number, totalPosterCreationsCount?: number): Promise<void> => {
    const posterCreationPromises: Promise<void>[] = [];

    showLoading(`createDesignFromUpload`, {text: window.i18next.t('pmwjs_creating')});
    for (let index = 0; index < items.length; index++) {
      posterCreationPromises.push(createSinglePagePosterFromItem(items[index], currentIndex ?? index + 1, totalPosterCreationsCount ?? items.length));
    }

    await Promise.all(posterCreationPromises);
    hideLoading(`createDesignFromUpload`);

    if (allCreatedPosterIds.length + failedUploads.length === (totalPosterCreationsCount ?? items.length)) {
      if (opts.onAllPostersCreated) {
        opts.onAllPostersCreated(allCreatedPosterIds, failedUploads);
      }
    }
  };

  const createMultipagePosterFromItems = async (items: MediaItemForPosterCreation[]): Promise<void> => {
    showLoading(`createDesignFromUpload`, {text: window.i18next.t('pmwjs_creating')});

    try {
      const newPosterHashedId = await createMultipagePosterAndGetHashedId(items);

      opts.onPosterCreated({
        newlyCreatedPosterHashedId: newPosterHashedId,
        currentIndex: 1,
        totalPosterCreationsCount: 1,
      });
      allCreatedPosterIds.push(newPosterHashedId);

      if (opts.onAllPostersCreated) {
        opts.onAllPostersCreated(allCreatedPosterIds, failedUploads);
      }
    } catch (error) {
      console.error(error);
      openErrorModal();
    } finally {
      hideLoading(`createDesignFromUpload`);
    }
  };

  const createSinglePagePosterFromItem = async (item: MediaItemForPosterCreation, currentIndex?: number, totalPosterCreationsCount?: number): Promise<void> => {
    try {
      const newPosterHashedId = await createSinglePagePosterAndGetHashedId(item);

      opts.onPosterCreated({
        newlyCreatedPosterHashedId: newPosterHashedId,
        currentIndex,
        totalPosterCreationsCount,
      });
      allCreatedPosterIds.push(newPosterHashedId);
    } catch (error) {
      failedUploads.push({name: `Failed to create poster for ${item.type} (${'hashedFilename' in item ? item.hashedFilename : 'deferred upload'})`} as File);
      console.error(error);
      openErrorModal();
    }
  };

  const createSinglePagePosterAndGetHashedId = async (item: MediaItemForPosterCreation): Promise<string> => {
    if (isDeferredImageUploadData(item)) {
      return createBlankPosterFromDeferredUploads([item]);
    }

    return (await window.PMW.writeLocal('poster/createPosterForMediaItem', {
      type: item.type,
      hashedFilename: item.hashedFilename,
      fid: opts.idFolder,
    })) as string;
  };

  const createMultipagePosterAndGetHashedId = async (items: MediaItemForPosterCreation[]): Promise<string> => {
    const deferredItems = items.filter(isDeferredImageUploadData);
    if (deferredItems.length === items.length) {
      return createBlankPosterFromDeferredUploads(deferredItems);
    }

    const itemsData = items
      .filter((item): item is ImageData | VideoData => !isDeferredImageUploadData(item))
      .map((item) => ({
        type: item.type,
        hashedFilename: item.hashedFilename,
      }));

    return (await window.PMW.writeLocal('poster/createMultipagePosterForMediaItems', {
      itemsData: JSON.stringify(itemsData),
      fid: opts.idFolder,
    })) as string;
  };

  /**
   * The editor-opening upload flow: create a blank poster sized to the uploads (one page
   * each), then stash the files keyed by the new poster's hashedId so the editor adds them
   * via its normal async upload on load. No image is uploaded or referenced before then.
   */
  const createBlankPosterFromDeferredUploads = async (deferredItems: DeferredImageUploadData[]): Promise<string> => {
    const itemsData = deferredItems.map((item) => ({width: item.width, height: item.height}));
    const posterHashedId = (await window.PMW.writeLocal('poster/createBlankPosterForImageUploads', {
      itemsData: JSON.stringify(itemsData),
      fid: opts.idFolder,
    })) as string;

    await storeDeferredEditorUploads(
      posterHashedId,
      deferredItems.map((item) => item.file)
    );
    return posterHashedId;
  };

  const onAddSelected = async (items: AddMediaItem[], currentUploadIndex?: number, totalUploadsCount?: number): Promise<void> => {
    const mediaItems = items.filter(isMediaItemForPosterCreation);

    if (!isUserLoggedIn()) {
      openAuthenticateModal({
        fbPermissions: '',
        signupSource: 'default',
        onLoginSuccess: async () => {
          await handlePosterCreation(mediaItems, currentUploadIndex, totalUploadsCount);
        },
      });
      return;
    }

    await handlePosterCreation(mediaItems, currentUploadIndex, totalUploadsCount);
  };

  const handlePosterCreation = async (items: MediaItemForPosterCreation[], currentUploadIndex?: number, totalUploadsCount?: number): Promise<void> => {
    const shouldCreateMultipage = opts.allowMultipagePosterCreation;

    if (!shouldCreateMultipage) {
      await createSinglePagePostersFromItems(items, currentUploadIndex, totalUploadsCount);
      return;
    }

    const areMultipleUploadsInProgress = totalUploadsCount !== undefined && totalUploadsCount > 1;

    if (areMultipleUploadsInProgress) {
      accumulatedItemsForMultipage.push(...items);

      if (accumulatedItemsForMultipage.length + failedUploads.length === totalUploadsCount) {
        await createMultipagePosterFromItems(accumulatedItemsForMultipage);
      }
      return;
    }

    await createMultipagePosterFromItems(items);
  };

  const onErrorDuringUpload = (file: File, currentUploadIndex?: number, totalItemsToUpload?: number): void => {
    failedUploads.push(file);

    if (opts.onErrorDuringUpload) {
      opts.onErrorDuringUpload(file, currentUploadIndex, totalItemsToUpload);
    }

    if (totalItemsToUpload !== undefined && allCreatedPosterIds.length + failedUploads.length === totalItemsToUpload) {
      if (opts.onAllPostersCreated) {
        opts.onAllPostersCreated(allCreatedPosterIds, failedUploads);
      }
    }
  };

  return {onAddSelected, onErrorDuringUpload};
};

window.PMW.mergePMW(PMW, {
  triggerCreateDesignFromUserUpload(opts: CreateDesignFromMediaModalOpts) {
    const allCreatedPosterIds: string[] = [];
    const failedUploads: File[] = [];
    const accumulatedItemsForMultipage: MediaItemForPosterCreation[] = [];

    const {onAddSelected, onErrorDuringUpload} = getSharedPosterCreationLogic({
      opts,
      allCreatedPosterIds,
      failedUploads,
      accumulatedItemsForMultipage,
    });

    if (opts.onTriggered) {
      opts.onTriggered();
    }

    openUserMediaModal({
      onAddSelectedCTAText: opts.footerPrimaryCtaText ?? window.i18next.t('pmwjs_create_design'),
      maxSelectionNumber: opts.maxSelectionNumber,
      onAddSelected,
      defaultTab: UploadsMediaType.IMAGE,
      mediaTypesToShow: [UploadsMediaType.IMAGE, UploadsMediaType.VIDEO],
      uploadAcceptedMimetype: 'image/*,video/*',
      onStartAddSelected: opts.onPosterCreationsStarted,
      onUploadError: onErrorDuringUpload,
      isForDesignUpload: true,
      hideFooterSecondaryActions: opts.hideFooterSecondaryActions,
      shouldDeferImageUploadsToEditor: opts.shouldUseFastPendingImageUploadFlow !== false,
    });
  },

  triggerCreateDesignFromAiImage(opts: CreateDesignFromMediaModalOpts) {
    const allCreatedPosterIds: string[] = [];
    const failedUploads: File[] = [];
    const accumulatedItemsForMultipage: MediaItemForPosterCreation[] = [];

    const {onAddSelected} = getSharedPosterCreationLogic({
      opts,
      allCreatedPosterIds,
      failedUploads,
      accumulatedItemsForMultipage,
    });

    if (opts.onTriggered) {
      opts.onTriggered();
    }

    openStockMediaModal({
      onStartAddSelected: opts.onPosterCreationsStarted,
      onAddSelected: onAddSelected,
      maxSelectionNumber: opts.maxSelectionNumber,
      tabsToShow: [StockPanelTabs.TEXT_TO_IMAGE],
      defaultSearchTab: StockPanelTabs.TEXT_TO_IMAGE,
      showTextToImageTool: true,
    });
  },
});
